import { useMutation } from '@tanstack/react-query';

import {
  postV1BusinessPartnerCurrentMigrationCandidateUserByIdAccept,
  postV1BusinessPartnerCurrentMigrationCandidateUserByIdReject,
  postV1IdentityCurrentMigrationCandidateBusinessPartnerByIdAccept,
  postV1IdentityCurrentMigrationCandidateBusinessPartnerByIdReject,
} from '../../api/generated/pm';
import {
  getV1BusinessPartnerCurrentMigrationCandidateUsersQueryKey,
  getV1IdentityCurrentMigrationCandidateBusinessPartnersQueryKey,
} from '../../api/generated/pm/@tanstack/react-query.gen';
import { queryClient } from '../../api/queryClient';
import { invalidateUsers } from '../../pages/UserManagement/userManagement.queries';
import { invalidateCustomerSuggestions } from './migration.queries';

/**
 * Mutation hooks for the TLM Migration feature.
 *
 * Each real mutation wraps the generated SDK call (with `throwOnError` so
 * failures reject) and invalidates the affected list via the generated query
 * key factory, following the colocated pattern (see
 * `pages/Settings/IdentityProviders/identityProviders.mutations.ts`).
 */

const invalidateMigrationCandidateBusinessPartners = () =>
  queryClient.invalidateQueries({
    queryKey: getV1IdentityCurrentMigrationCandidateBusinessPartnersQueryKey(),
  });

export const useRejectMigrationCandidateBusinessPartner = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { data } = await postV1IdentityCurrentMigrationCandidateBusinessPartnerByIdReject({
        path: { id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: invalidateMigrationCandidateBusinessPartners,
  });

export interface AcceptMigrationCandidatePayload {
  id: string;
  externalId: string;
}

export const useAcceptMigrationCandidateBusinessPartner = () =>
  useMutation({
    mutationFn: async ({ id, externalId }: AcceptMigrationCandidatePayload) => {
      const { data } = await postV1IdentityCurrentMigrationCandidateBusinessPartnerByIdAccept({
        path: { id },
        body: { externalId },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: invalidateMigrationCandidateBusinessPartners,
  });

// Awaiting a real endpoint — NOT the hey-api pattern; stays a stub until an API exists.
export const useMarkMigrationDataAsCorrect = () =>
  useMutation({
    mutationFn: async () => {
      // TODO: Replace with actual API endpoint when available
      await new Promise((resolve) => setTimeout(resolve, 500));
    },
    onSuccess: invalidateCustomerSuggestions,
  });

const invalidateMigrationCandidateUsers = () =>
  queryClient.invalidateQueries({
    queryKey: getV1BusinessPartnerCurrentMigrationCandidateUsersQueryKey(),
  });

/**
 * Accepts a Suggested User (migration candidate user). Best-effort on the
 * backend: becomes a User directly if an Identity with that email already
 * exists, otherwise an Invite to Join is created. No request body — the `id`
 * is the only input. Also invalidates the Users list: a directly-created
 * User must show up on the Users page without a manual reload.
 */
export const useAcceptMigrationCandidateUser = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { data } = await postV1BusinessPartnerCurrentMigrationCandidateUserByIdAccept({
        path: { id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      invalidateMigrationCandidateUsers();
      invalidateUsers();
    },
  });

/**
 * Rejects a Suggested User (migration candidate user). Wired to the review
 * modal's per-row trash button (`SuggestedUsersModal`) — an immediate,
 * irreversible server-side rejection with no confirm step, superseding SPEC
 * Q7's original local-deselect design.
 */
export const useRejectMigrationCandidateUser = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { data } = await postV1BusinessPartnerCurrentMigrationCandidateUserByIdReject({
        path: { id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: invalidateMigrationCandidateUsers,
  });
